/**
 * Override store (PROD-571).
 *
 * Holds the in-session list of structured override events captured by
 * `OverrideModal`. Per SPEC §5.1 the store is tenant-scoped; localStorage
 * keys are `cps:tenant:${tenantId}:overrides` so multi-tenant isolation
 * (HC6) is enforced at the persistence layer.
 *
 * `submitOverride(input)` is the single mutation entry point. It is
 * atomic per SPEC §5.4 invariant 3:
 *   1. Validate the structured payload against `overrideEventSchema`
 *      (HC7 LLM Guardrails: structured-output contract enforced at the
 *      boundary).
 *   2. Append to the in-memory `events` array.
 *   3. Persist to localStorage under the tenant-scoped key.
 *   4. Emit the `override_event` telemetry event with the validated
 *      payload (HC9 telemetry-real - the test in
 *      tests/override-store.test.ts asserts the actual emit against the
 *      real telemetry sink, not just that a handler was called).
 *   5. If persistence throws (quota / private browsing), revert the
 *      in-memory append, emit no telemetry, and surface
 *      `persistError`. The caller can retry or export.
 *
 * Telemetry-real criterion (HC9): the test in
 * tests/override-store.test.ts uses `vi.spyOn(telemetry, 'emitTelemetry')`
 * (not a swap or a stub) and asserts the resulting structured event JSON.
 *
 * The store does not auto-load persisted events on init. PROD-573 wires
 * the learning-loop event stream surface and decides hydration policy.
 */
import { create } from 'zustand';
import {
  buildOverrideEvent,
  overrideFormSchema,
  type OverrideEvent,
  type OverrideFormValues,
} from '../types/override';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';
import { useTenantStore } from './tenant.store';

const PERSIST_ERROR_MESSAGE =
  'Override could not be persisted. Submit again or export the event for manual capture.';

export interface SubmitOverrideInput {
  /** Pathway id the override belongs to. Required for tenant-scoped audit. */
  pathwayId: string;
  /** Step id the NBA was surfaced from. */
  stepId: string;
  /** NBA id being overridden. */
  nbaId: string;
  /** Form values captured by the modal. */
  form: OverrideFormValues;
}

export interface SubmitOverrideResult {
  ok: true;
  event: OverrideEvent;
}

export interface SubmitOverrideFailure {
  ok: false;
  reason: 'validation' | 'persistence';
  error: string;
}

export type SubmitOverrideOutcome = SubmitOverrideResult | SubmitOverrideFailure;

export interface OverrideStoreState {
  /** In-session override events. Newest entries are appended last. */
  events: OverrideEvent[];
  /**
   * Transient persistence-error message. Non-null when the most recent
   * `submitOverride` rolled back because `localStorage.setItem` threw.
   */
  persistError: string | null;
  /** Validate, append, persist, and emit telemetry. Atomic. */
  submitOverride: (input: SubmitOverrideInput) => SubmitOverrideOutcome;
  /** Clear the transient `persistError` (called on user dismissal). */
  clearPersistError: () => void;
  /** Reset the in-memory list. Used by tests + Reset Demo (Phase 3). */
  clear: () => void;
}

/** localStorage key per the SPEC §5.2 tenant-scoped naming convention. */
export function overridesStorageKey(tenantId: string): string {
  return `cps:tenant:${tenantId}:overrides`;
}

function persistOverrides(tenantId: string, events: OverrideEvent[]): void {
  if (typeof localStorage === 'undefined') {
    // Non-browser env (SSR / test setup before jsdom): treat as success.
    return;
  }
  localStorage.setItem(overridesStorageKey(tenantId), JSON.stringify(events));
}

export const useOverrideStore = create<OverrideStoreState>((set, get) => ({
  events: [],
  persistError: null,
  submitOverride: (input) => {
    // Step 1 - validate the form values against the Zod schema. HC7
    // structured-output contract: nothing reaches the sink without
    // passing parse.
    const parsed = overrideFormSchema.safeParse(input.form);
    if (!parsed.success) {
      const message = parsed.error.issues[0]?.message ?? 'Invalid override input';
      return { ok: false, reason: 'validation', error: message };
    }

    const tenantId = useTenantStore.getState().tenantId;
    const correlationId = newCorrelationId();
    let event: OverrideEvent;
    try {
      event = buildOverrideEvent({
        form: parsed.data,
        pathwayId: input.pathwayId,
        stepId: input.stepId,
        nbaId: input.nbaId,
        tenantId,
        correlationId,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid override event';
      return { ok: false, reason: 'validation', error: message };
    }

    // Step 2 - append in memory.
    const previous = get().events;
    const next = [...previous, event];
    set({ events: next });

    // Step 3 - persist to localStorage. If this throws, revert + surface
    // the persistError per SPEC §5.4 invariant 3 atomicity.
    try {
      persistOverrides(tenantId, next);
    } catch (err) {
      console.warn('[override.store] persist failed; reverting append', err);
      set({ events: previous, persistError: PERSIST_ERROR_MESSAGE });
      return {
        ok: false,
        reason: 'persistence',
        error: PERSIST_ERROR_MESSAGE,
      };
    }

    // Successful persist clears any stale persist error.
    if (get().persistError !== null) {
      set({ persistError: null });
    }

    // Step 4 - emit the telemetry event with the validated payload.
    // HC9 telemetry-real: the test asserts this exact emit against the
    // real telemetry module via vi.spyOn.
    emitTelemetry('override_event', {
      pathway_id: event.pathway_id,
      step_id: event.step_id,
      nba_id: event.nba_id,
      tenant_id: event.tenant_id,
      reason_category: event.reason_category,
      free_text_reason: event.free_text_reason,
      alternative_considered: event.alternative_considered,
      context_tags: event.context_tags,
      has_alternative_text: event.has_alternative_text,
      has_context_tags: event.has_context_tags,
      correlation_id: event.correlation_id,
      ts: event.ts,
    });

    return { ok: true, event };
  },
  clearPersistError: () => {
    if (get().persistError !== null) {
      set({ persistError: null });
    }
  },
  clear: () => {
    set({ events: [], persistError: null });
  },
}));
